import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, Row, Col } from 'antd';
import { SearchOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { URGENCY_LEVEL_OPTIONS } from '../../../constants/leadConstants';
import { LeadStatus, getFilterOptions, FilterOptions } from '../../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../../services/user';
import { getCurrentUser } from '../../../services/auth';

const { Option } = Select;

interface LeadFiltersProps {
  form: any;
  onSearch: () => void;
  onReset: () => void;
  onCreateLead: () => void;
  loading: boolean;
  activeTab?: string;
}

const LeadFilters: React.FC<LeadFiltersProps> = ({
  form,
  onSearch,
  onReset,
  onCreateLead,
  loading,
  activeTab = 'my-leads',
}) => {
  const isPublicPool = activeTab === 'public-pool';
  const [users, setUsers] = useState<UserAssignee[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    data_sources: [],
    provinces: [],
    cities: [],
    districts: [],
    urgency_levels: [],
    follow_statuses: [],
    lead_types: [],
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentUser = await getCurrentUser();
        setIsAdmin(currentUser.role === 'admin');

        // Load filter options
        const options = await getFilterOptions();
        setFilterOptions(options);

        if (isPublicPool && currentUser.role === 'admin') {
          const usersData = await getAssignableUsers({ limit: 100 });
          setUsers(usersData || []);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, [isPublicPool]);

  // 监听负责人变化，自动切换状态
  useEffect(() => {
    if (isPublicPool && isAdmin) {
      const assignedTo = form.getFieldValue('assigned_to');
      const currentStatus = form.getFieldValue('status');

      if (assignedTo && assignedTo.length > 0) {
        // 选择了负责人，自动切换到"已分配"
        if (currentStatus !== LeadStatus.ASSIGNED) {
          form.setFieldsValue({ status: LeadStatus.ASSIGNED });
        }
      } else {
        // 清空负责人，切换回"公海池"
        if (currentStatus === LeadStatus.ASSIGNED) {
          form.setFieldsValue({ status: LeadStatus.PUBLIC_POOL });
        }
      }
    }
  }, [form.getFieldValue('assigned_to')]);

  // 设置默认值
  useEffect(() => {
    if (isPublicPool) {
      const currentStatus = form.getFieldValue('status');
      if (!currentStatus) {
        form.setFieldsValue({ status: LeadStatus.PUBLIC_POOL });
      }
    }
  }, [isPublicPool, form]);

  return (
    <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
      {/* 第一行：新建线索、搜索、重置 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateLead}>
            新建线索
          </Button>
        </Col>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={onSearch}
              loading={loading}
            >
              搜索
            </Button>
            <Button icon={<FilterOutlined />} onClick={onReset}>
              重置
            </Button>
          </Space>
        </Col>
      </Row>

      {/* 筛选条件 */}
      <Row gutter={[16, 16]}>
        {/* 第一行 */}
        <Col span={6}>
          <Form.Item name="follow_status" label="跟进意向">
            <Select
              mode="multiple"
              placeholder="选择跟进意向（可多选）"
              allowClear
              maxTagCount="responsive"
              showSearch
              optionFilterProp="children"
            >
              {filterOptions.follow_statuses.map(status => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="urgency_level" label="紧急程度">
            <Select placeholder="选择紧急程度" allowClear>
              {URGENCY_LEVEL_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="lead_type" label="线索类型">
            <Select
              placeholder="选择线索类型"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {filterOptions.lead_types.map(type => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="data_source" label="数据来源">
            <Select
              placeholder="选择数据来源"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {filterOptions.data_sources.map(source => (
                <Option key={source} value={source}>
                  {source}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* 第二行 */}
        <Col span={6}>
          <Form.Item name="region" label="地区">
            <Input
              placeholder="搜索省份/城市/地区"
              prefix={<SearchOutlined />}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="keyword" label="关键词">
            <Input
              placeholder="搜索联系人、电话"
              prefix={<SearchOutlined />}
              allowClear
            />
          </Form.Item>
        </Col>

        {isPublicPool && isAdmin && (
          <Col span={6}>
            <Form.Item
              name="status"
              label="状态"
              initialValue={LeadStatus.PUBLIC_POOL}
            >
              <Select
                placeholder="选择状态"
                disabled={!isAdmin}
              >
                <Option value={LeadStatus.PUBLIC_POOL}>公海池</Option>
                {isAdmin && <Option value={LeadStatus.ASSIGNED}>已分配</Option>}
              </Select>
            </Form.Item>
          </Col>
        )}

        {isPublicPool && isAdmin && (
          <Col span={6}>
            <Form.Item name="assigned_to" label="负责人">
              <Select
                mode="multiple"
                placeholder="选择负责人（可多选）"
                allowClear
                maxTagCount="responsive"
                showSearch
                optionFilterProp="children"
                onChange={(value) => {
                  // 选择负责人时自动切换状态
                  if (value && value.length > 0) {
                    form.setFieldsValue({ status: LeadStatus.ASSIGNED });
                  } else {
                    form.setFieldsValue({ status: LeadStatus.PUBLIC_POOL });
                  }
                }}
              >
                {users.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.real_name || `用户${user.id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default LeadFilters;
